import { create } from 'zustand'
import type { Medication, MedicationCreate, MedicationUpdate, PendingDose, DoseStatus } from '@rondela/shared'
import { api, ApiError } from '../../shared/lib/api'

interface MedicationsState {
  medications: Medication[]
  pendingDoses: PendingDose[]
  isLoading: boolean
  error: string | null

  // Actions
  fetchMedicationsByPet: (petId: string) => Promise<void>
  fetchPendingDoses: () => Promise<void>
  createMedication: (petId: string, data: MedicationCreate) => Promise<Medication>
  updateMedication: (id: string, data: MedicationUpdate) => Promise<Medication>
  deleteMedication: (id: string) => Promise<void>
  logDose: (medicationId: string, notas?: string) => Promise<void>
  updateDose: (doseId: string, status: DoseStatus, notes?: string) => Promise<void>
  takeAllPetDoses: (petId: string) => Promise<number>
  batchUpdateDoses: (doseIds: string[], action: 'taken' | 'skipped' | 'missed', notes?: string) => Promise<void>
  clearError: () => void
  clearMedications: () => void
}

export const useMedicationsStore = create<MedicationsState>((set) => ({
  medications: [],
  pendingDoses: [],
  isLoading: false,
  error: null,

  fetchMedicationsByPet: async (petId: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ medications: Medication[] }>(`/api/pets/${petId}/medications`)
      set({ medications: response.medications, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar medicamentos'
      set({ error: message, isLoading: false })
    }
  },

  fetchPendingDoses: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.get<{ doses: PendingDose[] }>('/api/doses/pending')
      set({ pendingDoses: response.doses, isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al cargar dosis pendientes'
      set({ error: message, isLoading: false })
    }
  },

  createMedication: async (petId: string, data: MedicationCreate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ medication: Medication }>(`/api/pets/${petId}/medications`, data)
      set((state) => ({
        medications: [...state.medications, response.medication],
        isLoading: false,
      }))
      return response.medication
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al crear medicamento'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateMedication: async (id: string, data: MedicationUpdate) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.put<{ medication: Medication }>(`/api/medications/${id}`, data)
      set((state) => ({
        medications: state.medications.map((m) => (m.id === id ? response.medication : m)),
        isLoading: false,
      }))
      return response.medication
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar medicamento'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  deleteMedication: async (id: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.delete(`/api/medications/${id}`)
      set((state) => ({
        medications: state.medications.filter((m) => m.id !== id),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al eliminar medicamento'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  logDose: async (medicationId: string, notas?: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.post(`/api/medications/${medicationId}/doses`, { notas })
      set({ isLoading: false })
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al registrar dosis'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  updateDose: async (doseId: string, status: DoseStatus, notes?: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.put(`/api/doses/${doseId}`, { status, notes })
      set((state) => ({
        pendingDoses: state.pendingDoses.map((d) =>
          d.id === doseId ? { ...d, status } : d
        ).filter((d) => d.status === 'pending'),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar dosis'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  takeAllPetDoses: async (petId: string) => {
    try {
      set({ isLoading: true, error: null })
      const response = await api.post<{ count: number; message: string }>(
        `/api/pets/${petId}/doses/take-all`
      )
      // Remove all pending doses for this pet from local state
      set((state) => ({
        pendingDoses: state.pendingDoses.filter((d) => d.petId !== petId),
        isLoading: false,
      }))
      return response.count
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al marcar dosis como tomadas'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  batchUpdateDoses: async (doseIds: string[], action: 'taken' | 'skipped' | 'missed', notes?: string) => {
    try {
      set({ isLoading: true, error: null })
      await api.post('/api/doses/batch', { doseIds, action, notes })
      // Remove updated doses from pending list
      set((state) => ({
        pendingDoses: state.pendingDoses.filter((d) => !doseIds.includes(d.id)),
        isLoading: false,
      }))
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Error al actualizar dosis'
      set({ error: message, isLoading: false })
      throw error
    }
  },

  clearError: () => set({ error: null }),
  clearMedications: () => set({ medications: [], pendingDoses: [] }),
}))
